import { z } from "zod";
import {
  agentBehaviorSchema,
  agentRunRecordSchema,
  aiModeSchema,
  chatMessageSchema,
  chatSessionSchema,
  dodItemSchema,
  effortSchema,
  lastModelSchema,
  modelIdSchema,
  providerIdSchema,
  providerInfoSchema,
  runCeilingsSchema,
  uuidSchema,
} from "./aiTypes.ts";
import { MAX_CONTEXT_ITEMS_PER_MESSAGE } from "@racler/shared";
import { contextItemSchema } from "./context.ts";
import { changeSetCommands, changeSetInfoSchema, changeSetRequests } from "./changesets.ts";
import { commandCommands, commandRequests, commandViewSchema, resumeSchema } from "./commands.ts";
import { autocompleteCommands, autocompleteRequests } from "./autocomplete.ts";
import { request } from "./defs.ts";
import { desktopUpdateCommands } from "./desktopUpdate.ts";
import { projectIdSchema } from "./schemas.ts";

export * from "./aiTypes.ts";
export * from "./aiEvents.ts";
export * from "./usage.ts";
export * from "./changesets.ts";
export * from "./commands.ts";
export * from "./autocomplete.ts";

/**
 * AI protocol (milestone M2A, read-only Ask mode).
 *
 *   browser ──chat.*──► server (sessions, persistence, audit) ──ai.run/ai.cancel──► agent (provider + tools)
 *   browser ◄──events── server (persists, forwards) ◄──events────────────────────── agent
 *
 *   agent/ai.models   browser → agent (relayed like any agent request; credential-free model catalog)
 *   chatRequests      browser → server (the envelope carries machineId; answered by the server, never forwarded)
 *   agentCommands     server → agent ONLY (the relay never routes these from browsers)
 */

const empty = z.strictObject({});

export const aiModelsRequest = request(
  "agent",
  "ai.models",
  empty,
  z.strictObject({ providers: z.array(providerInfoSchema) }),
);

export const chatRequests = {
  ...autocompleteRequests,
  "agent/chat.sessions": request(
    "agent",
    "chat.sessions",
    z.strictObject({ projectId: projectIdSchema }),
    z.strictObject({ sessions: z.array(chatSessionSchema), lastModel: lastModelSchema.nullable() }),
  ),
  "agent/chat.session": request(
    "agent",
    "chat.session",
    z.strictObject({ sessionId: uuidSchema }),
    z.strictObject({
      session: chatSessionSchema,
      messages: z.array(chatMessageSchema),
      activeRunId: uuidSchema.nullable(),
      /** M10A: Active or recent durable run state. */
      activeRun: agentRunRecordSchema.nullable().optional(),
      /** Proposals of this chat (metadata only), so a reload restores pending and finished ChangeSets. */
      changeSets: z.array(changeSetInfoSchema),
      /** Commands requested by the model in this chat (Agent mode). */
      commands: z.array(commandViewSchema),
    }),
  ),
  "agent/chat.run": request(
    "agent",
    "chat.run",
    z.strictObject({ runId: uuidSchema }),
    z.strictObject({ run: agentRunRecordSchema.nullable() }),
  ),
  "agent/chat.send": request(
    "agent",
    "chat.send",
    z.strictObject({
      projectId: projectIdSchema,
      sessionId: uuidSchema.optional(),
      content: z.string().trim().min(1).max(20_000),
      provider: providerIdSchema,
      model: modelIdSchema,
      effort: effortSchema.optional(),
      /** Tool-permission policy for this message. Defaults to "chat" (read-only). */
      mode: aiModeSchema.default("chat"),
      behavior: agentBehaviorSchema.optional(),
      strictReview: z.boolean().optional(),
      dod: z.array(dodItemSchema).optional(),
      ceilings: runCeilingsSchema.optional(),
      contextItems: z.array(contextItemSchema).max(MAX_CONTEXT_ITEMS_PER_MESSAGE).optional(),
      environmentType: z.enum(["host", "worktree"]).optional(),
      environmentId: uuidSchema.optional(),
    }),
    z.strictObject({
      sessionId: uuidSchema,
      title: z.string(),
      runId: uuidSchema,
      userMessage: chatMessageSchema,
      assistantMessage: chatMessageSchema,
    }),
  ),
  "agent/chat.delete": request(
    "agent",
    "chat.delete",
    z.strictObject({ sessionId: uuidSchema }),
    z.strictObject({ deleted: z.boolean() }),
  ),
  ...changeSetRequests,
  ...commandRequests,
  "agent/chat.cancel": request(
    "agent",
    "chat.cancel",
    z.strictObject({ runId: uuidSchema }),
    z.strictObject({ cancelled: z.boolean() }),
  ),
} as const;

export const agentCommands = {
  /** Remote Desktop updates (V1): server -> agent only, never routable from a browser. */
  ...desktopUpdateCommands,
  ...changeSetCommands,
  ...commandCommands,
  ...autocompleteCommands,
  "agent/ai.run": request(
    "agent",
    "ai.run",
    z.strictObject({
      runId: uuidSchema,
      sessionId: uuidSchema,
      projectId: projectIdSchema,
      provider: providerIdSchema,
      model: modelIdSchema,
      effort: effortSchema.optional(),
      mode: aiModeSchema,
      behavior: agentBehaviorSchema.optional(),
      strictReview: z.boolean().optional(),
      dod: z.array(dodItemSchema).optional(),
      ceilings: runCeilingsSchema.optional(),
      environmentId: uuidSchema.optional(),
      baseCommit: z.string().regex(/^[0-9a-f]{7,40}$/i).optional(),
      /**
       * Continue a run that paused at an approval point (Agent mode). The AGENT injects the outcome it recorded
       * itself (command result / denial, ChangeSet apply result); the server never supplies tool results.
       */
      continueFrom: uuidSchema.optional(),
      resume: resumeSchema.optional(),
      contextItems: z.array(contextItemSchema).max(MAX_CONTEXT_ITEMS_PER_MESSAGE).optional(),
      /** Prior turns as plain text (tool results are not carried across turns). The last entry is the new question. */
      messages: z
        .array(z.strictObject({ role: z.enum(["user", "assistant"]), content: z.string().max(100_000) }))
        .min(1)
        .max(60),
    }),
    z.strictObject({ accepted: z.literal(true) }),
  ),
  "agent/ai.cancel": request(
    "agent",
    "ai.cancel",
    z.strictObject({ runId: uuidSchema }),
    z.strictObject({ cancelled: z.boolean() }),
  ),
} as const;
